import {
  Box,
  Stack,
  Typography,
  Select,
  MenuItem,
  Grid,
  Button,
  Divider,
  CircularProgress,
  Fade,
  Snackbar,
  Alert,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import TheatreTable from "./theatreTable";
import "../../../css/theatreTable.scss";
import { theme } from "../../../utilities/themes";
import { SeatsContext } from "../moviePage";
import getMovieDetails from "../utilities/getMovieDetails";
import getSeatLayout from "../utilities/getSeatLayout";
import reserveSeats from "../utilities/reserveSeats";
import { AuthContext } from "../../../context/auth/authContext";
import AuthDialog from "../../../components/authDialog";
import { vipRegex } from "../../../utilities/vipTest";

/**The details about the cinema showing a certain movie eg, Shows and their dates, seat layouts etc
 * @param {Object} props props
 * @param {string} props.MovieProviderID The id of the cinema
 * @param {string} props.MovieID The id of the movie
 * @param {string} props.ShowDate The date that will be used to get available shows
 * @param {Object} props.movieDetails The details of the movie
 * @param {Object} props.context An object representing seats context, I did this as a quick fix
 * because cinema content appears in a place that is not wrapped by seats context
 */
const CinemaContent = ({
  MovieProviderID,
  MovieID,
  ShowDate = "23 Aug",
  movieDetails,
}) => {
  const context = useContext(SeatsContext);
  /* #region  State  */
  const [MovieDates, setMovieDates] = useState([]);
  const [ShowDates, setShowDates] = useState([]);
  const [_selectedDay, _setSelectedDay] = useState("");
  const [_selectedShow, _setSelectedShow] = useState({});
  const [_selectedShowID, _setSelectedShowID] = useState("");
  const [movieDateIndex, setMovieDateIndex] = useState(0);
  const [detailsLoaded, setDetailsLoaded] = useState(false);
  const [tableLoaded, setTableLoaded] = useState(false);
  const [seat_data, setSeatData] = useState([]);
  const [reserving, setReserving] = useState(false);
  const authContext = useContext(AuthContext);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [ticketPrice, setTicketPrice] = useState(0);
  const [vipPrice, setVipPrice] = useState(0);
  const [error, setError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [ScreenIsVip, setScreenIsVip] = useState(false);
  const [PromoCode, setPromoCode] = useState("");
  const [PromoAmount, setPromoAmount] = useState(0);
  /* #endregion */

  /* #region  UI controllers */
  const finish = () => {
    context.setShow(_selectedShow);
    context.setShowDate(_selectedDay);
  };
  const changeDate = (event) => {
    let movieDIndex = ShowDates.indexOf(event.target.value);
    setMovieDateIndex(movieDIndex);
    _setSelectedDay(event.target.value);
    let show = MovieDates[movieDIndex].ShowDetails[0];
    changeShow(show);
  };

  const handleChangeShow = (event) => {
    let show = MovieDates[movieDateIndex].ShowDetails.find(
      (detail) => detail.ShowID === event.target.value
    );
    changeShow(show);
  };
  const changeShow = (show) => {
    setTableLoaded(false);
    getSeatLayoutData(MovieProviderID, show);
    setTicketPrice(show.TicketPrice);
    _setSelectedShowID(show.ShowID);
    _setSelectedShow(show);
  };
  /* #endregion */

  const getSeatLayoutData = (MovieProviderID, show) => {
    getSeatLayout(MovieProviderID, show.ScreenID, show.ShowID)
      .then(([layout, Markup, vipScreen]) => {
        context.setPromoAndCost({
          PromoCode,
          PromoAmount,
          MovieTicketCost: show.TicketPrice,
        });
        setSeatData(layout);
        setDetailsLoaded(true);
        context.setMarkup(Markup);
        setTableLoaded(true);
        setScreenIsVip(vipScreen);
        let vipRow = null;
        try {
          vipRow = layout.find((row) => row.SeatDescription.match(vipRegex));
        } catch (error) {}

        let vip = vipRow
          ? vipRow.SeatPrice.split(",").find((price) => parseInt(price))
          : null;

        setVipPrice(vip ? parseInt(vip) + Markup : 0);
      })
      .catch((error) => {
        setDetailsLoaded(true);
        setError("Something went wrong :(");
        setSnackbarOpen(true);
      });
  };
  useEffect(() => {
    getMovieDetails(MovieProviderID, MovieID, ShowDate)
      .then((data) => {
        if (data.MovieDates) {
          let show = data.MovieDates[0].ShowDetails[0];
          let showDates = data.MovieDates.map(
            (movieDate) => movieDate.ShowDates
          );
          setTicketPrice(show.TicketPrice);
          _setSelectedDay(data.MovieDates[0].ShowDates);
          _setSelectedShow(show);
          _setSelectedShowID(show.ShowID);

          //to set the content for the movie dates
          setShowDates(showDates);
          setMovieDates(data.MovieDates);
          setPromoAmount(data.PromoAmount);
          setPromoCode(data.PromoCode);

          getSeatLayoutData(MovieProviderID, show);
        } else {
          setDetailsLoaded(true);
          setError("Something went wrong :(");
          setSnackbarOpen(true);
        }
      })
      .catch((error) => {
        setDetailsLoaded(true);
        setError("Something went wrong :(");
        setSnackbarOpen(true);
      });
    // eslint-disable-next-line
  }, []);

  /**Reserve tickets */
  const reserveTickets = (phone) => {
    finish();
    setReserving(true);
    reserveSeats({
      MovieProviderID,
      MobileNumber: phone,
      MovieID,
      ScreenID: _selectedShow.ScreenID,
      Seats: Object.values(context.selectedSeats),
      ShowDate: _selectedDay,
      ShowID: _selectedShow.ShowID,
    })
      .then((response) => {
        setReserving(false);
        context.setActiveStep(1);
        //context.setTermsDialog(true);
      })
      .catch((error) => {
        setError("Something went wrong :(");
        setSnackbarOpen(true);
      });
  };
  return (
    <>
      {detailsLoaded ? (
        <>
          {error ? (
            <Fade in>
              <Stack
                minHeight={"40vh"}
                width="100%"
                justifyContent={"center"}
                alignItems={"center"}
              >
                <Typography variant="body1">
                  Unfortunately, there are no more available seats
                </Typography>
              </Stack>
            </Fade>
          ) : (
            <Fade in>
              <Box>
                <Stack spacing={4}>
                  {
                    //#region dropdowns for date time and screens of a show
                  }
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                      <Stack sx={{ width: "100%" }}>
                        <Typography
                          component="span"
                          color={theme.palette.primary.main}
                        >
                          {" "}
                          Select a date:
                        </Typography>
                        <Select
                          size="small"
                          value={_selectedDay}
                          onChange={changeDate}
                          sx={{
                            fontSize: {
                              xs: "0.8rem",
                              sm: "1rem",
                            },
                          }}
                        >
                          {ShowDates.map((date) => (
                            <MenuItem value={date} key={date}>
                              {date}
                            </MenuItem>
                          ))}
                        </Select>
                      </Stack>
                    </Grid>
                    {MovieDates.length > 0 && (
                      <Grid item xs={12} sm={3}>
                        <Stack sx={{ width: "100%" }}>
                          <Typography
                            component="span"
                            color={theme.palette.primary.main}
                          >
                            {" "}
                            Select a show:
                          </Typography>
                          <Select
                            size="small"
                            value={_selectedShowID}
                            onChange={handleChangeShow}
                            sx={{
                              fontSize: {
                                xs: "0.8rem",
                                sm: "1rem",
                              },
                            }}
                          >
                            {MovieDates[movieDateIndex].ShowDetails.map(
                              (showDetail) => (
                                <MenuItem
                                  value={showDetail.ShowID}
                                  key={
                                    showDetail.ShowID + showDetail.TicketPrice
                                  }
                                >
                                  {showDetail.ShowTime}&nbsp;
                                  {showDetail.ScreenName}
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </Stack>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={3}>
                      <Stack justifyContent={"end"} height="100%">
                        {!ScreenIsVip && (
                          <Typography variant="body1">
                            KES {ticketPrice} per seat{" "}
                            {vipPrice !== 0 && <>(Regular)</>}
                          </Typography>
                        )}

                        {vipPrice !== 0 && (
                          <Typography variant="body1">
                            KES {vipPrice} per seat (VIP)
                          </Typography>
                        )}
                      </Stack>
                    </Grid>
                  </Grid>
                  {
                    //#endregion
                  }
                  <Divider />
                  {tableLoaded ? (
                    <Fade in>
                      <Box>
                        <TheatreTable
                          onSeatSelect={context.onSeatSelect}
                          onSeatDeselect={context.onSeatDeselect}
                          selectedSeats={context.selectedSeats}
                          seatMarkup={context.markup}
                          seat_data={seat_data}
                          screenImg={movieDetails.MovieImageBig}
                        />
                        <Typography
                          component="span"
                          sx={{
                            fontSize: {
                              xs: "0.8rem",
                              sm: "1rem",
                            },
                          }}
                        >
                          {_selectedShow.ScreenDescription}
                        </Typography>
                      </Box>
                    </Fade>
                  ) : (
                    <Stack
                      minHeight={"30vh"}
                      width="100%"
                      justifyContent={"center"}
                      alignItems={"center"}
                    >
                      <CircularProgress size={30} />
                    </Stack>
                  )}
                </Stack>
                {
                  //#region A button for booking tickets and a text showing the total price
                }
                <Stack
                  direction="row-reverse"
                  sx={{ mt: 2, alignItems: "center" }}
                >
                  {Object.values(context.selectedSeats).length > 0 && (
                    <>
                      <Button
                        variant="contained"
                        onClick={() => {
                          if (authContext.loggedIn) {
                            reserveTickets(
                              authContext.userDetails.MobileNumber
                            );
                          } else {
                            setDialogOpen(true);
                          }
                        }}
                        disabled={reserving}
                        sx={{
                          fontSize: {
                            xs: "0.8rem",
                            sm: "1rem",
                          },
                        }}
                      >
                        {reserving && (
                          <>
                            <CircularProgress size={16} />
                            &nbsp;
                          </>
                        )}
                        {Object.values(context.selectedSeats).length > 1 ? (
                          <>Book tickets</>
                        ) : (
                          <>Book ticket</>
                        )}
                      </Button>
                      <Typography
                        variant="span"
                        sx={{
                          mr: 2,
                          fontSize: {
                            xs: "0.8rem",
                            sm: "1rem",
                          },
                        }}
                      >
                        {`KES ${context.totalCost}`}
                      </Typography>
                    </>
                  )}
                </Stack>
                {
                  //#endregion
                }
              </Box>
            </Fade>
          )}
        </>
      ) : (
        <Stack
          minHeight={"40vh"}
          width="100%"
          justifyContent={"center"}
          alignItems={"center"}
        >
          <CircularProgress size={30} />
        </Stack>
      )}
      <AuthDialog
        open={dialogOpen}
        setOpen={setDialogOpen}
        onLogin={(phone) => {
          reserveTickets(phone);
        }}
        onFail={(phone) => {
          reserveTickets(phone);
        }}
        title="Enter your phone number"
        subTitle="Please enter your phone number to continue"
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert severity={"error"} sx={{ width: "100%" }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CinemaContent;
